import { Pencil, RefreshCw, Trash2, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { Spinner } from "../../../components/ui/spinner";
import ConnectionIcon from "./ConnectionIcon";
import MCPStatusBadge from "./MCPStatusBadge";
import type { MCPDisplayStatus } from "../status";
import type { MCPConnection } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  connection: MCPConnection;
  status: MCPDisplayStatus;
  errorMessage?: string | null;
  isActive: boolean;
  canManage: boolean;
  isTogglingActive?: boolean;
  onToggleActive: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
};

const DetailHeader = ({
  connection,
  status,
  errorMessage,
  isActive,
  canManage,
  isTogglingActive = false,
  onToggleActive,
  onRefresh,
  isRefreshing = false,
  onEdit,
  onDelete,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const toggleLabel = t(
    isActive
      ? "mcpPage.actions.disableConnection"
      : "mcpPage.actions.enableConnection",
    { name: connection.name },
  );

  return (
    <header>
      <div className="flex h-[60px] min-w-0 items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 items-center gap-4">
          <ConnectionIcon connection={connection} size="sm" />
          <span className="truncate text-base font-normal text-foreground">
            {connection.name}
          </span>
        </div>
        <MCPStatusBadge status={status} title={errorMessage ?? undefined} />
      </div>

      <div className="flex h-[60px] items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <Switch
            checked={isActive}
            onCheckedChange={onToggleActive}
            disabled={!canManage || isTogglingActive}
            aria-label={toggleLabel}
            aria-busy={isTogglingActive}
            className="h-[18px] w-[33px] border-0 p-px [&>span]:size-4 [&>span[data-state=checked]]:translate-x-[15px] rtl:[&>span[data-state=checked]]:-translate-x-[15px]"
          />
          {isTogglingActive && <Spinner className="size-3.5" />}
          <span className="truncate text-sm text-muted-foreground">
            {isActive
              ? t("mcpPage.actions.enabled")
              : t("mcpPage.actions.disabled")}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {canManage && (
            <>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                onClick={onDelete}
                aria-label={t("mcpPage.actions.delete")}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onEdit}
                aria-label={t("mcpPage.actions.editDetails")}
              >
                <Pencil className="size-4" aria-hidden="true" />
              </Button>
            </>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label={t("mcpPage.actions.refresh")}
            aria-busy={isRefreshing}
          >
            {isRefreshing ? (
              <Spinner className="size-4" />
            ) : (
              <RefreshCw className="size-4" aria-hidden="true" />
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </header>
  );
};

export default DetailHeader;
